import React, { Suspense, useState, useEffect, useRef, lazy } from 'react';
import { APP_VERSION, APP_GIT_SHA, APP_BUILD_TITLE } from './lib/buildInfo';
import { stopSequenceProcessor } from './lib/sequenceProcessor';
import PartnerHeaderLogo from './components/PartnerHeaderLogo';
import CharlieWordmark from './components/CharlieWordmark';
import { AppProvider, useAppContext } from './lib/AppContext';
import { ConfirmProvider } from './components/shared/ConfirmProvider'; // #1912
import { supabase } from './lib/supabase';
import { MODULE_REGISTRY, buildRouteMap } from './lib/moduleRegistry';
import PasswordRecovery from './components/PasswordRecovery';
import LoginGate, { isAuthenticated, clearAuth, getCachedProfile, setCachedProfile } from './components/LoginGate';
import OnboardingWizard from './components/OnboardingWizard';
import LoadingScreen from './components/LoadingScreen';
import QuickScoutFloater from './components/QuickScoutFloater';
import QuickOrderHost from './modules/quickorder/QuickOrderHost';
import { openQuickOrder } from './modules/quickorder/quickOrderLauncher';
import PhoneMoreSheet from './components/PhoneMoreSheet';
// v1.7.41.81 (Ledger #1702) -- import removed, not just the render call.
// An unused import trips the same build-blocking lint rule (CI=true
// promotes ESLint warnings to errors) that already broke everyone's
// deploy once today on a different file -- not repeating that here.
// SidebarSwitchUser.jsx itself is untouched on disk, just not wired in.
import UserMenu from './components/UserMenu';
import HeaderPersonaChip from './components/HeaderPersonaChip'; // #2047
import { WELL_REPD_PARTNER_ID } from './modules/leadgen/lobby/checkin/checkinShared';
import { useIsMobile } from './lib/useIsMobile';
import { useQualifiedCount } from './lib/useQualifiedCount';
import { swipeDismiss } from './lib/swipeDismiss';
import PageContainer from './components/PageContainer';
import { crumbsOf, hashFor, keyFromHash, parentKey, sidebarKeyFor } from './lib/navigation';
import UpdateToast from './components/UpdateToast';
import StaleBanner from './components/StaleBanner';
import { reloadNow } from './lib/appUpdate';
import { atVeryTop, confirmRefresh } from './lib/pullToRefresh';
import { compactCount } from './lib/compactCount';
import OutboxMenuBadge from './modules/genserv/OutboxMenuBadge'; // GS-0171 step 7: waiting-for-approval count beside Outbox
import { GENSERV_PARTNER_ID, isGenservPartner, genservSidebar, inGenservContext, showQuickFloater, GENSERV_PHONE_NAV } from './lib/genservShell';
import { boundedCall } from './lib/genservBoundedCall';
import AppRouteGate from './components/AppRouteGate'; // WO-2929 AC-03: per-app route refusal
import useAppMemberships from './lib/useAppMemberships';
import { appsAvailableToUser } from './lib/appIdentity';
import { startKey, rememberShell, phoneStartsInGenserv, genservOverridden, withRetry, accessFrom, CHOSE_CHARLIE_KEY } from './lib/coldStart';
import { applyHistory, REPLACE, backToOrigin } from './lib/historyNav';
import { useCurrentCrumbTitle, withCrumbTitle } from './lib/crumbTitle';
import { THEME_2026 } from './lib/theme2026';
import { useTheme } from './lib/useTheme';
import { reportError } from './lib/errorReporter';
import { resolveWo2894Route } from './lib/wo2894Entry';
import { defaultEntryPartner } from './lib/personaPreference';

// v1.7.40 (QB v18, 2026-08-06) — appShellSkin import removed, sidebar
// now uses a flat color, real, iterating on the exact shade -- was
// #3B4F61, now #375971 per Shane's live feedback (first try too dark).
// Real asset itself stays at src/assets/skins/app-shell-skin.png in
// case a future partner-specific skin wants the buckskin texture.
// v1.7.41.82 (Ledger #1702) -- wellrepdMark import removed here, moved
// to UserMenu.jsx where the image now actually renders. Same lint
// discipline as the SidebarSwitchUser removal above -- an unused
// import trips the same build-blocking rule that already broke CI
// once today.
import PersonaSwitchGlow from './components/PersonaSwitchGlow';
import { setViewMemoryUser, noteMenuNavigation, menuNavigationSettled } from './lib/viewMemory';
import './App.css';
import './components/foundation/foundation.css';

export function applyDefaultEntryPersona(partner, activePartner, setSessionPartner) {
  if (partner && activePartner?.id !== partner.id) setSessionPartner(partner);
}

export function resolveAndApplyDefaultEntryPersona({ hash, partners, userId, activePartner, setSessionPartner, canAccessPartner, accessKnown = true }) {
  if (!accessKnown) return null;
  const partner = defaultEntryPartner({ hash, partners, userId, canAccessPartner });
  applyDefaultEntryPersona(partner, activePartner, setSessionPartner);
  return partner;
}

// v1.7.40.52 — real, permanent fix for a real, repeated problem tonight:
// manually keeping package.json's version field in sync with each real
// commit message is a chicken-and-egg trap (editing the file to describe
// commit N necessarily happens BEFORE commit N exists, so it's always
// one step behind at best, and silently drifts further if a bump gets
// skipped -- confirmed happened twice in a row tonight, Shane caught
// both). Real fix: the version now comes from REACT_APP_GIT_VERSION,
// injected at BUILD TIME (package.json's build script) by parsing the
// leading "vX.Y.Z.NN" off the actual latest git commit MESSAGE -- which
// Vercel only reads AFTER that commit is already pushed and checked out,
// so there's no ordering problem and no manual editing step to forget.
// pkg.version stays as a local-dev fallback only (e.g. running the app
// outside a git checkout), never the source of truth in production.

// v1.7.40.60 -- real, reverted per Shane's direct call (QB v20,
// 2026-08-07): the per-user Initial-icon + name-remainder "My
// Dashboard" nav treatment (icon reads as "S", followed by "hane") was
// an old, already-decided-against edit. It had gone silently dormant
// since a separate real bug (currentUser staying null on any returning
// session) meant this code path never actually fired -- fixing that
// bug in v1.7.40.59 accidentally brought it back to life, which is
// what Shane caught here. SIDEBAR_INITIAL_ICONS and
// sidebarDashboardNameRemainder (and their now-orphaned nameplate
// imports) are removed outright rather than left dead, per the
// standing dead-code-doesn't-linger practice -- the dashboard nav item
// now renders through the same real SIDEBAR_NAV_ICONS path every other
// module uses, plain label, no special case.

const ROUTE_MAP = buildRouteMap();

// v1.7.39.192 — real helper: distinguishes the new PNG icon set (imported
// module paths, e.g. from ./assets/icons/*.png) from the old placeholder
// strings ('ICONS.search') and real raw SVG path data. Bundler-resolved
// image imports come through as a string ending in a real image extension
// or as a data: URI -- neither of which real path data or 'ICONS.x' ever is.
// v1.7.40.35 — real, dedicated sidebar nav icon set. The glossy 3D PNG
// icons (hunt.png, lobby.png, crm.png, etc) look great on light Settings/
// launcher tile cards -- rounded white card, drop shadow, made for a light
// background -- but were being reused as-is in the dark navy sidebar,
// where the light card reads as a mismatched floating box (Shane's direct
// flag, screenshot, 2026-08-07: "this has got to get fixed looks
// horrible"). Real fix, per the already-logged Relay finding ("needs a
// genuinely different treatment, simple line-art, not glossy 3D"): a
// separate, sidebar-only icon set using lucide-react stroke icons, which
// already share currentColor/stroke styling with the active-state color
// logic below. Deliberately NOT touching moduleRegistry.icon (the PNG
// paths) -- those still render correctly on HuntLauncher, Lobby, and other
// light tile-grid pages, this map only overrides what the SIDEBAR itself
// renders.
// v1.7.40.36 — real, permanent sidebar nav icon set (replaces the same-
// day lucide-react interim). Custom Marty artwork, matching Charlie's
// actual module subjects, delivered as dark-navy-on-white line art per
// the work order -- recolored here (white fill, alpha preserved from
// the original stroke coverage) so they read clean against the dark
// sidebar without needing Marty to redeliver in a different palette.
// Public booking page (book.wellrepd.com or #/book) — no auth, no shell
const BookingPublic = lazy(() => import('./modules/appointments/BookingPublic'));

function isPublicBookingRoute() {
  try {
    const host = (window.location.hostname || '').toLowerCase();
    if (host === 'book.wellrepd.com') return true;
    const hash = (window.location.hash || '').replace(/^#\/?/, '').split('?')[0];
    return hash === 'book' || hash.startsWith('book/');
  } catch (e) { return false; }
}

// #1783 -- real per-partner booking pages, #/book/{slug} in addition
// to bare #/book. Real, deliberate: falls back to null (default Well
// Repd brand, current book.wellrepd.com behavior unchanged) for both
// the bare route and any unrecognized slug -- BookingPublic itself
// handles a null/not-found partner gracefully via api/booking-partner.js's
// real 200-with-null response.
function getBookingPartnerSlug() {
  try {
    const hash = (window.location.hash || '').replace(/^#\/?/, '').split('?')[0];
    if (hash.startsWith('book/')) return hash.slice('book/'.length) || null;
    return null;
  } catch (e) { return null; }
}

// #1705 -- real public partner showcase (partners.wellrepd.com or
// #/partners) -- no auth, no shell, same real convention as the
// booking page above.
const PartnersShowcase = lazy(() => import('./modules/partners/PartnersShowcase'));

function isPublicPartnersRoute() {
  try {
    const host = (window.location.hostname || '').toLowerCase();
    if (host === 'partners.wellrepd.com') return true;
    const hash = (window.location.hash || '').replace(/^#\/?/, '').split('?')[0];
    return hash === 'partners';
  } catch (e) { return false; }
}

// #2005 -- real FBCL member portal (fbcl.wellrepd.com or #/fbcl) -- no
// CHARLIE auth, no Shell, same real bypass convention as booking/partners
// above. Demo phase: lives in this codebase reusing CHARLIE's components;
// full build later gets its own independent deployment (locked decision).
const FbclApp = lazy(() => import('./fbcl/FbclApp'));

function isFbclRoute() {
  try {
    const host = (window.location.hostname || '').toLowerCase();
    if (host === 'fbcl.wellrepd.com') return true;
    const hash = (window.location.hash || '').replace(/^#\/?/, '').split('?')[0];
    return hash === 'fbcl' || hash.startsWith('fbcl/');
  } catch (e) { return false; }
}

// v1.6.94: header persona chip — always-visible dropdown so the operator
// always sees which persona is active and can override in one click.

// v1.7.41.90 (Ledger #1468) -- theme toggle, in the HEADER not Settings.
// Stinger's direct correction to the obvious guess: this is used daily,
// so burying it behind Settings would be wrong. It sits beside the
// persona chip, which is the other thing he checks constantly.
//
// THREE states, not two: light / dark / system. A two-way toggle would
// make "follow my system preference" unreachable once someone had
// picked either side -- and system IS the locked default.
// #2753: Quick Order, one quiet button in the header on a computer (a phone entry comes later). It opens the flow; nothing is asked or sent from here.
function HeaderQuickOrder() {
  return (
    <button
      type="button"
      onClick={() => openQuickOrder()}
      title="Take an order after a call: say it or type it, review, email the partner"
      style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: '4px 8px', color: 'var(--sidebar-text-muted)' }}
    >
      Quick Order
    </button>
  );
}

function SidebarTonePicker({ collapsed }) {
  const { preference, setTheme } = useTheme();
  return <div aria-label="Color tone" style={{ display: 'flex', justifyContent: collapsed ? 'center' : 'flex-end', gap: 4, padding: '6px 12px 8px' }}>
    {['light', 'medium', 'dark'].map((tone) => <button className="sidebar-tone-dot" data-tone={tone} key={tone} type="button" aria-label={`${tone} tone`} aria-pressed={preference === tone} title={`${tone[0].toUpperCase()}${tone.slice(1)} tone`} onClick={() => setTheme(tone)} />)}
  </div>;
}

// v1.6.94: recognize stale-chunk errors from a new deploy. When a user has an
// old tab open and we ship new JS, React tries to lazy-load chunk hashes that
// no longer exist on the server and throws ChunkLoadError. Auto-reload once
// (guarded by sessionStorage) to hydrate fresh chunks instead of showing a
// red "Module Crashed" wall.
function isChunkLoadError(err) {
  if (!err) return false;
  const name = String(err.name || '');
  const msg = String(err.message || '');
  if (name === 'ChunkLoadError') return true;
  if (/Loading chunk [\w-]+ failed/i.test(msg)) return true;
  if (/Loading CSS chunk [\w-]+ failed/i.test(msg)) return true;
  if (/error loading dynamically imported module/i.test(msg)) return true;
  return false;
}

// Error boundary so a crash inside a module shows the error rather than
// nuking the whole app to a blank screen.
class ModuleErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null, info: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    // A stale-chunk error after a deploy never reloads the page by itself (that wiped what he was doing). The boundary below shows a New Version Available note with a Reload button.
    this.setState({ info });
    console.error('[CHARLIE module crash]', error, info);
    reportError('boundary', error, { componentStack: info && info.componentStack, appVersion: APP_VERSION });
  }
  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null, info: null });
    }
  }
  render() {
    if (this.state.error) {
      // v1.6.94: if a chunk error survived the auto-reload (network still bad,
      // second-time failure), show a friendlier prompt than the raw stack.
      if (isChunkLoadError(this.state.error)) {
        return (
          <div style={{ padding: 32, background: 'var(--gov-page-bg)', minHeight: '100%' }}>
            <div style={{ background: '#ffffff', border: '1px solid var(--gov-slate-300)', borderRadius: 8, padding: 20, maxWidth: 640 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gov-text-primary)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>New Version Available</div>
              <div style={{ fontSize: 14, color: 'var(--gov-text-primary)', marginBottom: 14 }}>Charlie was updated in the background. Reload to load the latest version.</div>
              <button onClick={() => { try { window.sessionStorage.removeItem('charlie_chunk_reload'); } catch (_) {} reloadNow(); }} style={{ background: 'var(--gov-brand-red)', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontWeight: 600, cursor: 'pointer' }}>Reload Charlie</button>
            </div>
          </div>
        );
      }
      return (
        <div style={{ padding: 32, background: 'var(--gov-page-bg)', minHeight: '100%' }}>
          <div style={{ background: '#ffffff', border: '1px solid var(--gov-slate-200)', borderRadius: 8, padding: 20, maxWidth: 720 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#991b1b', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Module Crashed</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--gov-text-primary)', marginBottom: 10 }}>{String(this.state.error.message || this.state.error)}</div>
            <pre style={{ fontSize: 11, color: 'var(--gov-slate-600)', background: 'var(--gov-slate-50)', padding: 10, borderRadius: 6, overflow: 'auto', maxHeight: 240, whiteSpace: 'pre-wrap' }}>{(this.state.error.stack || '') + '\n' + ((this.state.info && this.state.info.componentStack) || '')}</pre>
            <button onClick={() => { try { window.sessionStorage.removeItem('charlie_chunk_reload'); } catch (_) {} this.setState({ error: null, info: null }); }} style={{ marginTop: 12, background: 'var(--gov-brand-red)', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 14px', fontWeight: 600, cursor: 'pointer' }}>Reset</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// A password-recovery email link lands here with type=recovery in the URL. Read it before Supabase strips it from the address bar.
const RECOVERY_AT_LOAD = typeof window !== 'undefined' && /type=recovery/.test(String(window.location.hash || '') + String(window.location.search || ''));

export default function App() {
  const [recovering, setRecovering] = useState(RECOVERY_AT_LOAD);
  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange((event) => { if (event === 'PASSWORD_RECOVERY') setRecovering(true); });
    return () => { try { data?.subscription?.unsubscribe?.(); } catch (_) {} };
  }, []);
  const [authed, setAuthed] = useState(isAuthenticated());
  // v1.7.40.59 -- real, root-cause fix. currentUser previously only got
  // set via LoginGate's onAuth callback -- but `authed` initializes
  // straight from isAuthenticated() (a cached-session check), which
  // means on any returning/refreshed session LoginGate never mounts,
  // onAuth never fires, and currentUser silently stays null for the
  // rest of that browser session. This is why the new Switch User
  // toggle (and potentially other currentUser-gated UI) rendered
  // nothing for Shane -- currentUser?.profile?.role was undefined, not
  // a rendering/visibility problem. Real fix: hydrate currentUser
  // directly from the same cached profile isAuthenticated() already
  // checked for, so a returning session has real, correct data
  // immediately instead of waiting on a login flow that will never run.
  const [currentUser, setCurrentUser] = useState(() => {
    const cached = getCachedProfile();
    return cached ? { user: { id: cached.id, email: cached.email }, profile: cached } : null;
  });

  // v1.7.40.61 -- real, closes the remaining gap in the v1.7.40.59 fix.
  // The optimistic hydration above fixes currentUser being null, but it
  // trusts whatever was last written to localStorage at login time --
  // if that's stale (e.g. Shane's own browser had a profile cached from
  // before super_admin was set, or before .select('*') included role),
  // it silently stays wrong forever, since a returning session never
  // re-runs LoginGate's real fetch. Real fix: always do one real
  // background revalidation against Supabase on mount, regardless of
  // whether the cache looked fine -- silently corrects currentUser (and
  // the cache itself) if the real row differs, no visual flash, no
  // logout required. This is the permanent, self-healing version of
  // "sign out and back in" rather than relying on Shane doing that by
  // hand every time this class of staleness comes up.
  // #2771: the view memory (where Back returns to) is kept per signed-in person.
  useEffect(() => { setViewMemoryUser(currentUser && currentUser.user && currentUser.user.id); }, [currentUser?.user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!authed) return;
    let cancelled = false;
    (async () => {
      // GS-0077: only a REAL answer of "no session" signs the person out. A refresh that could not reach the server (one bar, offline) or one that hangs leaves the cached sign-in in
      // place: the app opens from the phone and the check runs again next time. Signing out here used to throw a field user to a login screen that needs signal.
      let res = null;
      try { res = await boundedCall(supabase.auth.getSession(), 8000); } catch (e) { return; }
      if (cancelled) return;
      const session = res?.data?.session;
      if (!session?.user) {
        if (res?.error) return; // any error is "could not check", not "signed out"
        // A cached profile with NO session behind it (an expired phone session): the app was showing itself as signed in. Show the login screen instead.
        console.warn('[charlie] cached profile but no session: showing the login screen'); // eslint-disable-line no-console
        clearAuth();
        setAuthed(false);
        setCurrentUser(null);
        return;
      }
      let profile = null;
      try {
        const r = await boundedCall(supabase.from('user_profiles').select('*').eq('id', session.user.id).single(), 8000);
        profile = r?.data || null;
      } catch (e) { return; }
      if (cancelled || !profile) return;
      setCachedProfile(profile);
      setCurrentUser({ user: { id: session.user.id, email: profile.email }, profile });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  // Public booking page — bypass auth and shell entirely
  if (isPublicBookingRoute()) {
    return (
      <Suspense fallback={<div style={{ padding: 32, color: 'var(--gov-slate-500)' }}>Loading…</div>}>
        <BookingPublic partnerSlug={getBookingPartnerSlug()} />
      </Suspense>
    );
  }

  // #1705 -- real public partner showcase, same bypass pattern
  if (isPublicPartnersRoute()) {
    return (
      <Suspense fallback={<div style={{ padding: 32, color: 'var(--gov-slate-500)' }}>Loading…</div>}>
        <PartnersShowcase />
      </Suspense>
    );
  }

  // #2005 -- FBCL member portal, same bypass pattern. Own auth, own
  // data (fbcl schema), never touches CHARLIE's Shell or staff auth.
  if (isFbclRoute()) {
    return (
      <Suspense fallback={<div style={{ padding: 32, color: 'var(--gov-slate-500)' }}>Loading…</div>}>
        <FbclApp />
      </Suspense>
    );
  }

  function handleAuth(user, profile) {
    setCurrentUser({ user, profile });
    setAuthed(true);
  }

  function handleSignOut() {
    clearAuth();
    setAuthed(false);
    setCurrentUser(null);
  }

  if (recovering) {
    return <PasswordRecovery onDone={() => { setRecovering(false); try { window.history.replaceState(null, '', window.location.pathname); } catch (_) {} window.location.reload(); }} />;
  }

  if (!authed) return <LoginGate onAuth={handleAuth} />;

  // Real, #1337. Forces the onboarding wizard on first login -- blocks
  // Shell entirely until user_profiles.onboarding_completed is true.
  // Existing users default true (see the real DB migration note on
  // #1337) so this only ever fires for a genuinely new hire.
  if (currentUser?.profile && currentUser.profile.onboarding_completed === false) {
    return (
      <OnboardingWizard
        user={currentUser.user}
        profile={currentUser.profile}
        onComplete={(updatedProfile) => {
          setCachedProfile(updatedProfile);
          setCurrentUser({ user: currentUser.user, profile: updatedProfile });
        }}
      />
    );
  }

  return (
    <AppProvider>
      <ConfirmProvider>
        <Shell currentUser={currentUser} onSignOut={handleSignOut} />
      </ConfirmProvider>
    </AppProvider>
  );
}

function Shell({ currentUser, onSignOut }) {
  const { activePartner, partners, activePartners, chooseActivePartner, setSessionPartner, personaSwitchSignal, viewMode, setViewMode, loading, toast, showToast, dismissToast, viewAsUserId, setViewAsUserId, loadPartners, ensureAppData } = useAppContext();

  // v1.7.40.47 — real, Side Notes Session 7 (Tech Sheets item, finally
  // greenlit): sidebar sub-link per partner under Tech Sheets, auto-shown
  // only if that partner actually has real tech sheet files, plus a real
  // manual override (partners.tech_sheets_hidden_override) so a partner
  // can be hidden from this list without deleting its files -- Shane's
  // stated use case, winding down Fox Underground without losing its
  // tech sheets. Dynamic, so it can't live as a static moduleRegistry
  // subItems array like the other accordions; built here and spliced
  // into the module list just for the 'tech-sheets' entry at render time.
  const [techSheetsLinks, setTechSheetsLinks] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!partners || partners.length === 0) { setTechSheetsLinks([]); return; }
      const [{ data: fileRows }, { data: partnerRows }] = await Promise.all([
        supabase.from('files').select('partner_id').eq('file_type', 'techsheet'),
        supabase.from('partners').select('id, name, tech_sheets_hidden_override'),
      ]);
      if (cancelled) return;
      const counts = {};
      (fileRows || []).forEach((r) => { counts[r.partner_id] = (counts[r.partner_id] || 0) + 1; });
      const links = (partnerRows || [])
        .filter((p) => (counts[p.id] || 0) > 0 && !p.tech_sheets_hidden_override)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((p) => ({ key: `tech-sheets:${p.id}`, label: p.name, partnerId: p.id }));
      setTechSheetsLinks(links);
    })();
    return () => { cancelled = true; };
  }, [partners]);

  // Real, used everywhere the sidebar renders module rows -- replaces the
  // static 'tech-sheets' entry's subItems with the live per-partner list
  // computed above. Every other module passes through unchanged.
  // GenServ shell (#2503): with GenServ as the active partner the left menu is the GenServ-only menu; any other partner gets the normal Charlie menu.
  // WO-2929: a person whose only active membership is GenServ gets the GenServ menu and shell whatever persona is selected (people with no membership rows are unchanged).
  const appMemberships = useAppMemberships(currentUser?.user?.id);
  const membershipApps = appsAvailableToUser(appMemberships || []);
  const genservOnlyByMembership = membershipApps.length > 0 && !membershipApps.includes('charlie') && membershipApps.includes('genserv');
  const genservShellOn = isGenservPartner(activePartner) || genservOnlyByMembership;
  const genservMenu = genservShellOn ? genservSidebar(MODULE_REGISTRY) : null;
  const sidebarModules = genservMenu || MODULE_REGISTRY.map((m) => (
    m.key === 'tech-sheets' && techSheetsLinks.length > 0
      ? { ...m, subItems: techSheetsLinks }
      : m
  ));

  // Real helper: a Tech Sheets partner sub-item is "active" when we're on
  // the real tech-sheets route AND that specific partner is the active
  // persona -- its synthetic key never becomes activeKey (navigate()
  // always lands on the real 'tech-sheets' route), so the normal
  // activeKey === sub.key check alone can't detect it.
  function isSubItemActive(sub) {
    if (sub.partnerId) return activeKey === 'tech-sheets' && activePartner?.id === sub.partnerId;
    return activeKey === sub.key;
  }
  function clickSubItem(sub) {
    noteMenuNavigation(); // #2771: a sidebar click is a fresh visit
    if (sub.partnerId) {
      const p = partners.find((x) => x.id === sub.partnerId);
      if (p) switchPartner(p);
      navigate('tech-sheets');
    } else if (sub.quickQuote) {
      // #1879, real: routes to the same 'quote-to-invoice' key as The
      // Rig itself, not a separate registry entry -- it IS the same
      // screen. Real edge case handled: clickSubItem doesn't normally
      // force a remount (only the top-level-item re-click handlers
      // at lines ~910/~1131 do), so clicking Quick Quote while
      // already sitting on The Rig would otherwise silently do
      // nothing -- same real remountNonce convention used there,
      // applied here so this always actually fires.
      if (activeKey === 'quote-to-invoice') setRemountNonce((n) => n + 1);
      navigate('quote-to-invoice', { quickQuote: true });
    } else {
      navigate(sub.key);
    }
  }


  // v1.6.94: clear the ChunkLoadError reload guard once we've mounted
  // successfully. If we're rendering, chunks loaded fine — next stale-chunk
  // event should get a fresh auto-reload attempt.
  useEffect(() => {
    try { window.sessionStorage.removeItem('charlie_chunk_reload'); } catch (_) {}
  }, []);

  // Start the sequence processor once the user is authed. Runs every 60s,
  // sends emails during weekday business hours (7am-6pm CT). Silent tick log.
  // v1.7.27b — the client-side processor is DISABLED. Sequence sending now runs SERVER-SIDE
  // via /api/sequence-cron (Vercel cron), so it works 24/7 regardless of whether a browser
  // tab is open, and isn't subject to stale-bundle caching. Kept the import for the reply
  // checker only. (Historic bug: the client processor was fragile/inconsistent and let
  // multi-step sequences silently stall.)
  useEffect(() => {
    return () => stopSequenceProcessor(); // ensure any prior loop is stopped
  }, []);

  // Wrapper: switch partner + show silent toast
  function switchPartner(p) {
    chooseActivePartner(p);
    showToast(`Switched to ${p.name}`, 'info');
  }
  // GS-0050: "Back to Charlie" at the bottom of the GenServ menu. Returns to the persona that was active before GenServ (else the default Well Repd partner, else the
  // first non-GenServ partner), session-only like the automatic restore, and opens the normal Charlie dashboard.
  function backToCharlie() {
    const target = prevPartnerRef.current
      || (partners || []).find((x) => x.id === WELL_REPD_PARTNER_ID)
      || (partners || []).find((x) => x.id !== GENSERV_PARTNER_ID);
    if (target) setSessionPartner(target);
    sessionEnteredRef.current = false;
    try { window.sessionStorage.setItem(CHOSE_CHARLIE_KEY, '1'); } catch (e) { /* private mode */ }
    setViewMode('personal');
    navigate('dashboard');
  }
  // GS-0179: the GenServ phone More page has a Back to Charlie row; it asks the shell through this event (the shell owns the partner switch).
  const backToCharlieRef = useRef(null);
  backToCharlieRef.current = backToCharlie;
  useEffect(() => {
    const h = () => backToCharlieRef.current();
    window.addEventListener('genserv:back-to-charlie', h);
    return () => window.removeEventListener('genserv:back-to-charlie', h);
  }, []);
  // Read initial route from the URL hash so reload/back stays in CHARLIE
  function readHashKey() {
    try {
      return resolveWo2894Route(keyFromHash(window.location.hash, (k) => !!ROUTE_MAP[k])); // old #/home links now open Dashboard
    } catch (e) { return 'dashboard'; }
  }
  // v1.7.41.74 (Ledger #1430) -- REAL, the missing half of an already
  // half-built feature. readHashKey() has stripped a '?...' query
  // string out of the hash since it was written, but nothing ever read
  // that query string back in -- routeParams only ever came from an
  // in-app navigate(key, params) call, reset to {} on every reload,
  // bookmark visit, or back/forward press. That's not real deep-linking,
  // just in-memory params that don't survive the one thing "deep-link"
  // actually means: a URL you can copy, paste, reload, or go back to
  // and land on the same real sub-state. Verified before building: no
  // react-router in package.json (row's original finding, still true),
  // and the popstate/pushState/dead-route-redirect infrastructure this
  // depends on is real and already working -- confirmed by reading it
  // directly, not assumed. Only this one piece was missing.
  function readHashParams() {
    try {
      const q = (window.location.hash || '').split('?')[1];
      if (!q) return {};
      const out = {};
      new URLSearchParams(q).forEach((v, k) => { out[k] = v; });
      return out;
    } catch (e) { return {}; }
  }
  const [activeKey, setActiveKey] = useState(() => startKey(readHashKey(), { hostname: window.location.hostname }));
  const [genservAccess, setGenservAccess] = useState(null); // null = not known yet
  const [genservAccessCheckedFor, setGenservAccessCheckedFor] = useState(null);
  const pipeMarketEntryHandled = useRef(false);
  useEffect(() => {
    const userId = currentUser?.user?.id;
    const accessKnown = !!userId && genservAccessCheckedFor === userId;
    if (pipeMarketEntryHandled.current || !partners?.length || !userId || !accessKnown) return;
    const canAccessPartner = (partner) => !isGenservPartner(partner) || genservAccess === true;
    const partner = resolveAndApplyDefaultEntryPersona({ hash: window.location.hash, partners, userId, activePartner, setSessionPartner, canAccessPartner, accessKnown });
    if (!partner) { pipeMarketEntryHandled.current = true; return; }
    pipeMarketEntryHandled.current = true;
  }, [partners, activePartner, setSessionPartner, currentUser?.user?.id, genservAccess, genservAccessCheckedFor]);
  // Opening straight on Lead Management does not load the app-wide contacts, new arrivals and companies (the other screens read them): they start the moment the person goes anywhere else.
  useEffect(() => { if (activeKey !== 'lead-management' && activeKey !== 'lobby' && ensureAppData) ensureAppData(); }, [activeKey, ensureAppData]);
  // GenServ shell (#2503, GS-0037). Opening a GenServ screen (a menu click, a link, or a reload straight onto #/genserv/...) puts GenServ in as the active partner for THIS session
  // only (nothing is saved, so the next visit comes back in the partner the user chose), and only for people with access to GenServ. Leaving the GenServ screens restores the
  // partner they were in. Picking another partner by hand while on a GenServ screen is respected.
  useEffect(() => {
    const uid = currentUser?.user?.id;
    if (!uid) return undefined;
    setGenservAccess(null);
    if (currentUser?.profile?.role === 'super_admin') { setGenservAccess(true); setGenservAccessCheckedFor(uid); return undefined; }
    let live = true;
    // GS-0076: a failed check is retried and, if it still fails, leaves access unknown (null): it never counts as "no access", which used to leave the phone on the Charlie dashboard.
    withRetry(() => supabase.from('user_partner_access').select('id').eq('user_id', uid).eq('partner_id', GENSERV_PARTNER_ID).limit(1))
      .then((r) => { if (live) { setGenservAccess(accessFrom(r)); setGenservAccessCheckedFor(uid); } });
    return () => { live = false; };
  }, [currentUser?.user?.id, currentUser?.profile?.role]);
  // GS-0076: a person whose only partner is GenServ always lands in GenServ (once per session, so Back to Charlie still works), and the shell they were last in is remembered so a fresh
  // phone start comes back to it (src/lib/coldStart.js).
  const [genservOnly, setGenservOnly] = useState(false);
  useEffect(() => {
    const uid = currentUser?.user?.id;
    if (!uid || currentUser?.profile?.role === 'super_admin') { setGenservOnly(false); return undefined; }
    let live = true;
    withRetry(() => supabase.from('user_partner_access').select('partner_id').eq('user_id', uid))
      .then(({ data, error }) => { if (live) setGenservOnly(!error && !!data && data.length > 0 && data.every((r) => r.partner_id === GENSERV_PARTNER_ID)); });
    return () => { live = false; };
  }, [currentUser?.user?.id, currentUser?.profile?.role]);
  const onlyHandledRef = useRef(false);
  useEffect(() => {
    if (!genservOnlyByMembership || inGenservContext(activeKey)) return;
    navigate('genserv'); // WO-2929: a GenServ-only membership never lands on, or stays on, a Charlie screen
  }, [genservOnlyByMembership, activeKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!genservOnly || onlyHandledRef.current) return;
    onlyHandledRef.current = true;
    if (!String(activeKey).startsWith('genserv') && !inGenservContext(activeKey)) navigate('genserv');
  }, [genservOnly]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (genservAccess !== true) return;
    if (isGenservPartner(activePartner) && inGenservContext(activeKey)) rememberShell('genserv');
    else if (!inGenservContext(activeKey)) rememberShell('charlie');
  }, [activeKey, activePartner, genservAccess]);
  const prevKeyRef = useRef(null);
  const prevPartnerRef = useRef(null);
  const sessionEnteredRef = useRef(false);
  const coldHandledRef = useRef(false);
  const entrySignalRef = useRef(0); // personaSwitchSignal when GenServ was entered: a later change means the person picked a partner by hand
  const coldDefaultRef = useRef(false);
  useEffect(() => {
    const prev = prevKeyRef.current; prevKeyRef.current = activeKey;
    if (!inGenservContext(activeKey)) {
      if (sessionEnteredRef.current) {
        sessionEnteredRef.current = false;
        if (isGenservPartner(activePartner) && prevPartnerRef.current) setSessionPartner(prevPartnerRef.current);
      }
      return;
    }
    if (!String(activeKey).startsWith('genserv') || genservAccess !== true || !activePartner || !partners || partners.length === 0) return;
    // GS-0076: the saved partner can be put back after GenServ was entered (partners reload on boot, a token refresh, a partner edit) with no pick by the person. That is an override, not
    // a choice: enter GenServ again. A hand pick bumps personaSwitchSignal and is respected.
    const overridden = genservOverridden({ entered: sessionEnteredRef.current, genservActive: isGenservPartner(activePartner), signal: personaSwitchSignal, entrySignal: entrySignalRef.current });
    const entering = !inGenservContext(prev) || !coldHandledRef.current || overridden;
    coldHandledRef.current = true;
    if (!entering || isGenservPartner(activePartner)) return;
    const g = partners.find((x) => x.id === GENSERV_PARTNER_ID);
    if (g) { prevPartnerRef.current = activePartner; sessionEnteredRef.current = true; entrySignalRef.current = personaSwitchSignal; setSessionPartner(g); }
  }, [activeKey, activePartner, partners, genservAccess, setSessionPartner, personaSwitchSignal]);
  // GS-0076: a person with GenServ access who opens the app on a phone starts in the GenServ shell, unless they chose Charlie earlier in this browser session (Back to Charlie). Runs
  // once, when access becomes known, and only when nothing but the default Home was asked for, so a deep link is never taken over.
  useEffect(() => {
    if (coldDefaultRef.current || genservAccess === null) return;
    coldDefaultRef.current = true;
    let chose = false;
    try { chose = !!window.sessionStorage.getItem(CHOSE_CHARLIE_KEY); } catch (e) { /* private mode */ }
    const explicitDashboard = /^#\/?dashboard(?:\?|$)/.test(window.location.hash || '');
    if (phoneStartsInGenserv({ access: genservAccess, key: activeKey, width: window.innerWidth, choseCharlie: chose, explicitDashboard, hostname: window.location.hostname })) navigate('genserv');
  }, [genservAccess]); // eslint-disable-line react-hooks/exhaustive-deps
  // Real, system-wide fix, Shane 2026-08-06: several modules (Lobby
  // confirmed, likely others) keep their own "landing vs sub-view"
  // state internally (e.g. Lobby's showWelcome), not as part of the
  // real route. Re-clicking the SAME already-active sidebar item calls
  // navigate() with a key that hasn't changed -- React correctly treats
  // that as a no-op, so the component never remounts and internal
  // state like showWelcome never resets. Real fix: a nonce that only
  // increments on a genuine re-click of the active item, folded into
  // ActiveComponent's key below to force a real, clean remount.
  const [remountNonce, setRemountNonce] = useState(0);
  // #2771: the page a sidebar click replaces has gone: its cleanup (which must not record a return) has run, so recording is allowed again.
  useEffect(() => { menuNavigationSettled(); }, [activeKey, remountNonce]);
  const isMobile = useIsMobile();
  const [routeParams, setRouteParams] = useState(readHashParams());
  const [expandedGroups, setExpandedGroups] = useState({});
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('charlie.sidebar.collapsed.v1') === 'true'; } catch (_) { return false; }
  });
  const crumbTitleNow = useCurrentCrumbTitle(); // the open record's name (a contact, company, partner...), shown as the last crumb
  const qualifiedCount = useQualifiedCount(isMobile); // badge on the phone's Leads tab
  const [moreMenu, setMoreMenu] = useState(false); // v1.7.17 — mobile 'More' drawer (full module list)
  const [personaSwitcher, setPersonaSwitcher] = useState(false);
  const switcherRef = useRef();
  const color = activePartner?.brand_color || '#0077cc';

  // v1.7.39.208 -- real, Shane's catch 2026-08-04: on mobile, backgrounding
  // and reopening the app (iOS PWA/Safari) often doesn't trigger a real
  // page reload -- React state like moreMenu/personaSwitcher just stays
  // exactly as it was, so if the More Menu was open when you left, it's
  // still open when you come back. Real fix: force both closed every time
  // the app becomes visible again after being backgrounded.
  useEffect(() => {
    function handleVisibility() {
      if (document.visibilityState === 'visible') {
        setMoreMenu(false);
        setPersonaSwitcher(false);
      }
    }
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Safety net: moving to another page always closes the menu and persona drawers (and their dimming backdrops), so none can be left over the next page.
  useEffect(() => { setMoreMenu(false); setPersonaSwitcher(false); }, [activeKey]);

  // v1.7.18 — Pull-to-refresh (mobile). Pull down from the very top to reload the latest deploy.
  const [pullY, setPullY] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  useEffect(() => {
    let startY = 0, pulling = false;
    const THRESHOLD = 70;
    const onStart = (e) => {
      // Only arm when the main content (the thing that really scrolls) and the touched list are at the very top.
      if (atVeryTop(e.target)) {
        startY = e.touches[0].clientY; pulling = true;
      } else { pulling = false; }
    };
    const onMove = (e) => {
      if (!pulling) return;
      const dy = e.touches[0].clientY - startY;
      if (dy > 0) setPullY(Math.min(dy * 0.5, 90));
    };
    const onEnd = () => {
      if (pulling && pullY > THRESHOLD) {
        // Never a surprise reload: ask first, and keep his place when he says yes.
        setPullY(0);
        if (confirmRefresh()) { setRefreshing(true); setTimeout(() => reloadNow(), 300); }
      } else {
        setPullY(0);
      }
      pulling = false;
    };
    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onEnd, { passive: true });
    window.addEventListener('touchcancel', onEnd, { passive: true }); // iOS cancels touches mid-gesture; never leave the pull state stuck
    return () => {
      window.removeEventListener('touchcancel', onEnd);
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
    };
  }, [pullY]);

  useEffect(() => {
    function handle(e) {
      if (switcherRef.current && !switcherRef.current.contains(e.target)) setPersonaSwitcher(false);
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  // opts.replace: a redirect or fallback swaps the current history entry instead of adding one (see lib/historyNav.js, docs/browser-back-navigation.md).
  function navigate(key, params = {}, opts = {}) {
    setActiveKey(key);
    setRouteParams(params);
    // v1.7.30 — auto-open the parent group of the destination once (seed expanded=true),
    // so the active group starts open but can still be collapsed. `expanded` is now the
    // SINGLE source of truth for whether a submenu shows (no more `|| isActive` force-open).
    // v1.7.40.44 — real fix, Side Notes Session 7 Item 14: this only ever
    // OPENED the destination's parent group -- when the destination has no
    // parent (a flat page like Lobby) it silently did nothing, leaving
    // whatever accordion was previously open (e.g. HUNT) still expanded.
    // Confirmed isolated to exactly that case: navigating to a CRM sub-item
    // correctly closed HUNT because it found a real parent (crm) and the
    // object-literal set replaced state entirely; flat pages never reached
    // that replace at all. Real fix: also clear expandedGroups when the
    // destination is a flat page -- but NOT when the destination is an
    // accordion parent itself (HUNT, CRM, etc. clicked directly), since
    // that click's own toggleGroup() call already ran a moment earlier in
    // the same handler and this would otherwise immediately stomp it.
    try {
      const parent = MODULE_REGISTRY.find((m) => m.subItems?.some((s) => s.key === key));
      const isAccordionParent = MODULE_REGISTRY.some((m) => m.key === key && m.subItems);
      if (parent) setExpandedGroups((p) => (p[parent.key] ? p : { [parent.key]: true }));
      else if (!isAccordionParent) setExpandedGroups({});
    } catch (e) { /* ignore */ }
    // Push internal route into browser history so back button moves through
    // CHARLIE screens instead of leaving the app entirely.
    try {
      // v1.7.41.74 (Ledger #1430) -- real deep-linking. Only real,
      // serializable primitives (string/number/boolean) go into the URL
      // -- an object or array param silently could not round-trip
      // through URLSearchParams anyway, and silently dropping it here
      // is more honest than putting "[object Object]" in a real URL a
      // partner might copy and send to Stinger.
      const qp = new URLSearchParams();
      Object.entries(params || {}).forEach(([k, v]) => {
        if (v !== null && v !== undefined && (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean')) {
          qp.set(k, String(v));
        }
      });
      const qs = qp.toString();
      const url = hashFor(key, qs); // hierarchical: #/settings/calendar-connections
      applyHistory(window.history, window.location.hash, url, { charlieKey: key, from: activeKey }, opts); // `from` lets a Back-to button tell whether the page it names is the one behind us
    } catch (e) { /* ignore */ }
  }

  // Sync state when user presses browser back/forward
  useEffect(() => {
    function onPop() {
      const key = readHashKey();
      setActiveKey(key);
      // v1.7.41.74 (Ledger #1430) -- was setRouteParams({}), which threw
      // away real params on every back/forward press even though the
      // URL still had them. That was half the reason deep-linking never
      // really worked -- read the real URL instead of assuming empty.
      setRouteParams(readHashParams());
    }
    window.addEventListener('popstate', onPop);
    // Seed initial entry so the first back press has somewhere to go
    try {
      if (!window.location.hash) {
        window.history.replaceState({ charlieKey: activeKey }, '', hashFor(activeKey));
      }
    } catch (e) { /* ignore */ }
    return () => window.removeEventListener('popstate', onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // v1.7.39.144 — real bug Shane hit live: my-dashboard was removed from
  // moduleRegistry.js in v1.7.39.140, but his browser still had that
  // hash active (bookmark/reopened tab), landing on a permanent dead
  // end ("Module not found: my-dashboard") with no way back except
  // manually editing the URL. activeKey is read straight from
  // window.location.hash on load -- nothing ever validated it against
  // the real, current MODULE_REGISTRY. Real fix, not a one-off patch
  // for this specific route: if the resolved route doesn't exist at
  // all, redirect to dashboard automatically. Covers this case and any
  // future module rename/removal the same way. Placed here (before the
  // `if (loading) return` below) deliberately -- a hook placed after
  // that early return would violate Rules of Hooks (inconsistent hook
  // order between the loading and loaded render passes).
  // An old flat link (#/calendar-connections) is rewritten, in place, to its hierarchical address (no new history entry, so Back never loops).
  useEffect(() => {
    if (!activeKey || !ROUTE_MAP[activeKey]) return;
    try {
      const [path, query] = (window.location.hash || '').split('?');
      const want = hashFor(activeKey);
      if (path !== want) window.history.replaceState({ charlieKey: activeKey }, '', `${want}${query ? `?${query}` : ''}`);
    } catch (e) { /* ignore */ }
  }, [activeKey]);

  useEffect(() => {
    if (activeKey && !ROUTE_MAP[activeKey]) {
      navigate('dashboard', {}, REPLACE); // a dead address is swapped for Dashboard, so Back does not return to it and bounce again
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey]);

  function toggleGroup(key) {
    setExpandedGroups((p) => {
      const nowOpen = !p[key];
      // v1.7.23 — when opening a group (esp. Settings at the bottom), scroll its expanded
      // items into view so they aren't trapped below the fold.
      if (nowOpen) {
        setTimeout(() => {
          const el = document.getElementById(`navgroup-${key}`);
          if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 60);
      }
      // Real, 2026-08-06: exclusive accordion, Shane's ask. Opening a
      // group now closes every other one -- was a plain object where
      // multiple groups could stay expanded at once. Closing (nowOpen
      // === false) still just closes that one group, same as before.
      return nowOpen ? { [key]: true } : { ...p, [key]: false };
    });
  }

  // v1.7.39.196 — real splash fade-out tracking, same hook-ordering
  // discipline as the redirect effect above: must live before the
  // `if (loading) return` early return or it violates Rules of Hooks.
  // Keeps LoadingScreen mounted a beat longer than `loading` itself so
  // the zoom+fade transition (Marty's item #5) actually has time to play
  // instead of the splash just vanishing the instant data arrives.
  const [showSplash, setShowSplash] = useState(loading);
  const [splashFadeOut, setSplashFadeOut] = useState(false);
  useEffect(() => {
    if (loading) {
      setShowSplash(true);
      setSplashFadeOut(false);
    } else if (showSplash) {
      setSplashFadeOut(true);
      const t = setTimeout(() => setShowSplash(false), 650);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  if (loading) {
    return <LoadingScreen fadeOut={splashFadeOut} />;
  }

  const ActiveComponent = ROUTE_MAP[activeKey];
  // The sidebar item that is lit for this page: the page itself when the sidebar lists it, else its nearest listed ancestor (Settings stays selected on a Settings page).
  const sidebarListed = (k) => genservMenu ? genservMenu.some((m) => m.key === k) : MODULE_REGISTRY.some((m) => !m.hidden && (m.key === k || (m.subItems || []).some((x) => x.key === k && !x.hidden)));
  const navKey = sidebarKeyFor(activeKey, sidebarListed);

  const grouped = {
    // Real (#1882), found while auditing for the same real gap #1875
    // fixed in PartnersManager.jsx: Well Repd Consulting's group_type
    // is 'Parent Company', which nothing here handled -- it was
    // completely missing from this list (which powers the mobile
    // Switch Persona drawer below), not just miscategorized.
    'Parent Company': partners.filter((p) => p.group_type === 'Parent Company' && p.active !== false),
    // v1.7.06 — 3 tiers: Tier 1 (oilfield partners we rep), Tier 2 (others), Private (my LLCs)
    'Tier 1': partners.filter((p) => (p.group_type === 'Tier 1' || p.group_type === 'oilfield') && p.active !== false),
    'Tier 2': partners.filter((p) => (p.group_type === 'Tier 2' || p.group_type === 'entrepreneurial' || p.group_type === 'volunteer') && p.active !== false),
    'Private': partners.filter((p) => p.group_type === 'Private' && p.active !== false),
  };

  // v1.7.40 (QB v18, 2026-08-06) — real, renderedGroups removed. Only
  // existed to place the now-removed gold nav-group divider.


  // Mobile bottom nav items: Leads (Qualified badge), Contacts, Dashboard, Persona. (No Compose: an email is written from the contact.)
  const MOBILE_NAV = [
    { key: 'mobile-capture', icon: 'M12 3v18m9-9H3', label: 'Mobile view' },
    { key: 'lead-management', icon: 'M3 5h2v2H3V5zm4 0h14v2H7V5zM3 11h2v2H3v-2zm4 0h14v2H7v-2zM3 17h2v2H3v-2zm4 0h14v2H7v-2z', label: 'Leads', badge: true },
    { key: 'contacts', icon: 'M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z', label: 'Contacts' },
    { key: 'dashboard', icon: 'M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z', label: 'Dashboard' },
    { key: 'partner_switcher_mobile', icon: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z', label: 'Persona', isPersona: true },
  ];

  return (
    <div style={{ display: 'flex', height: '100vh', background: '#f0f2f5', color: 'var(--gov-text-primary)', fontFamily: 'system-ui, -apple-system, sans-serif', overflow: 'hidden' }}>
      {/* The splash fades out OVER the app (fixed, click-through while fading), so the screen starts loading while it plays instead of after it. */}
      {showSplash && <LoadingScreen fadeOut={splashFadeOut} />}

      {/* #1451 -- persona-switch glow, mounted at shell root so it fires
          regardless of which page is showing when the switch happens. */}
      <PersonaSwitchGlow switchSignal={personaSwitchSignal} />

      {/* v1.7.18 — pull-to-refresh indicator (mobile) */}
      {(pullY > 0 || refreshing) && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', height: refreshing ? 44 : pullY, zIndex: 600, color, fontSize: 12, fontWeight: 700, pointerEvents: 'none', transition: refreshing ? 'height 0.2s' : 'none' }}>
          {refreshing ? 'Refreshing…' : (pullY > 70 ? '↻ Release to refresh' : '↓ Pull to refresh')}
        </div>
      )}

      {/* ── SIDEBAR ── */}
      {/* v1.7.39.192 — real app shell skin applied here only. Header bar is
          currently white with dark text (var(--gov-text-primary) on #ffffff) -- applying the
          same dark leather texture there would break legibility across every
          page and needs its own coordinated text-color pass. Real, deliberate
          scope cut, flagged to Shane rather than forced in blind. */}
      {/* v1.7.40 (QB v18, 2026-08-07) — real, the app's official, locked
          2026 design system palette. Shane's exact spec, not a sampled
          guess: primary #141C2A, sidebar #1B2433, hover #243A4D, brass
          accent #C9A14A. The sidebar ("the lapel," Shane's real, official
          term for it now) uses the real gradient built from the sampled
          reference shades (Navy Highlight -> Shadow Navy) for the rich,
          non-flat navy-to-black feel from the reference art, rather than
          the earlier flat #375971. Single source of truth now lives in
          src/lib/theme2026.js -- import from there for any future work
          touching these colors instead of hardcoding new hex values. */}
      {/* v1.7.40 (QB v18, 2026-08-07) — real, Shane's ask: "the left lapel
          needs to be all hamburger menu due to space limitations" on
          mobile. Real, confirmed: mobile already has a fully-working
          hamburger nav -- the 'More' drawer (moreMenu state, v1.7.17),
          triggered from the bottom mobile nav bar, showing the complete
          module list. The real bug was this full desktop sidebar ALSO
          rendering on mobile alongside it -- genuinely redundant, eating
          real screen width. Real, safe fix: hide via display, not a JSX
          restructure -- this deeply-nested block (logo zone, scripture
          strip, persona switcher, full nav) is real, high blast-radius
          to manually re-wrap at this hour; a display toggle on the
          existing div achieves the same real outcome with zero
          structural risk. */}
      <div className="charlie-sidebar" style={{ width: sidebarCollapsed ? 64 : 220, background: THEME_2026.lapelGradient, borderRight: '1px solid var(--foundation-look-line)', display: isMobile ? 'none' : 'flex', flexDirection: 'column', flexShrink: 0, transition: 'width 160ms ease' }}>

        {/* Logo zone — fixed height 56px, ready for partner logos.
            Real, Shane 2026-08-06: white background so real partner
            logos actually shine, was inheriting the dark leather
            texture from the sidebar. Real, text colors flipped dark
            to match -- were white, designed for the old dark bg. */}
        {/* v1.7.40 (QB v18, 2026-08-06) — real, Shane flagged the Grand
            Slam Caps logo "crammed in left corner...too small, should be
            enlarged". Real root cause: non-Well-Repd partner logos were
            capped at maxHeight:48/maxWidth:180 inside a left-aligned flex
            row -- a wide/short logo like GSC's renders small and hugs the
            edge. Real fix: bigger real ceiling (60px/220px) + centered
            justify for partner logos specifically. Well Repd's own
            wordmark keeps its real, deliberate left-aligned layout
            (justifyContent only flips for OTHER partners). */}
        {/* v1.7.40 (QB v18, 2026-08-06) — real, second pass after Shane
            reviewed live screenshots of 10 real partner logos. First pass
            (60px/220px) missed a real physical constraint: the sidebar
            itself is only 220px wide, so a 220px maxWidth leaves ZERO
            real buffer after the 18px padding each side -- wide wordmark
            logos (FoxUnderground 8.4:1, Maverick 6.7:1, Phoenix 5.1:1)
            were rendering as thin slivers jammed against the edge, not
            "crammed" from being too small but from having no headroom.
            Real fix, measured against actual real logo dimensions for
            all 19 partners: maxWidth tightened to 165 (guarantees real
            buffer within the true 184px usable space), maxHeight raised
            to 66 (square/badge logos -- Wildcat 1.4:1, Sasquatch 1.9:1,
            He Wins 1.5:1, GSC 1.7:1 -- were bottlenecked on height, not
            width, so this is what actually makes them read larger).
            Header grew 78->86 to keep real vertical breathing room
            proportional to the taller image. Real, honest limitation:
            the very widest wordmarks (FoxUnderground, Maverick, Phoenix)
            will still render shorter than the square logos -- physically
            inherent to fitting an 8:1 wordmark in a 220px-wide sidebar,
            not fixable without either a wider sidebar (separate, bigger
            layout decision) or Marty cropping those specific logos
            tighter to their real mark. */}
        {/* #2033 -- real fix, Stinger direct 2026-09-04: v1.7.41.82's
            reasoning above ("the generic logo_url path is fine for Well
            Repd now") turned out wrong in practice -- Well Repd Consulting
            genuinely has a logo_url set (confirmed directly against the
            partners table), so this branch was rendering the Well Repd
            wordmark image as the sidebar's top identity element every
            time the active persona was Well Repd itself -- which is the
            default/most common state, not an edge case. That is exactly
            the "Well Repd logo is the most visually prominent top-of-
            sidebar element" problem #2033 was opened to fix.
            Real fix: the CHARLIE wordmark below is CHARLIE's own native
            identity (typography/spacing already matches the sidebar,
            not a pasted image) -- it only needs to win specifically when
            the active persona IS Well Repd. Every other real partner
            persona keeps rendering its own logo exactly as before,
            unchanged. Well Repd still has real representation -- its
            wordmark already lives in UserMenu.jsx per #1702, unchanged
            by this fix. */}
        {/* WO-2912 -- the sidebar carries CHARLIE's own wordmark for every persona. The active partner's logo (partners.logo_url) now lives in the page header (PartnerHeaderLogo). */}
        <div data-testid="sidebar-identity" style={{ height: 72, background: 'transparent', borderBottom: '1px solid rgba(201,162,39,0.35)', display: 'flex', alignItems: 'center', justifyContent: sidebarCollapsed ? 'center' : 'flex-start', padding: sidebarCollapsed ? '0 8px' : '0 18px', flexShrink: 0, gap: 12 }}>
          <CharlieWordmark
            wordmarkTestId="sidebar-wordmark"
            eTestId="sidebar-wordmark-e"
            sublineTestId="sidebar-subline"
            fontSize={sidebarCollapsed ? 11 : 16}
            letterSpacing={sidebarCollapsed ? 0 : 3}
            showSubline={!sidebarCollapsed}
            sublineSize={9}
            sublineSpacing="0.1em"
            sublineMarginTop={2}
            title={sidebarCollapsed ? 'Charlie' : undefined}
          >
            {!sidebarCollapsed && <div data-testid="build-version" title={APP_BUILD_TITLE} style={{ fontSize: 8, color: THEME_2026.wordmarkGrey, opacity: 0.7, letterSpacing: '0.04em', marginTop: 1 }}>v{APP_VERSION}</div>}
          </CharlieWordmark>
        </div>

        {/* #1797 -- real, Stinger direct 2026-08-23: verse strip removed
            from here entirely, redundant now that #1796's dashboard
            header carries the daily KJV verse in its own real slot.
            Reclaims this vertical space for the nav below. The
            underlying VerseStrip component itself isn't deleted -- see
            PersonalDashboard.jsx, which now renders it (as variant=
            "header") in the dashboard's own consolidated header block. */}

        <nav data-testid="sidebar-nav" style={{ flex: 1, overflowY: 'auto', padding: '10px 0' }}>
          {sidebarModules.map((mod) => {
            if (mod.hidden || mod.phoneOnly) return null;
            // Per-user restriction — hide modules not in restrictTo for current user
            if (mod.restrictTo && Array.isArray(mod.restrictTo)) {
              const email = (currentUser?.user?.email || '').toLowerCase();
              if (!mod.restrictTo.map((e) => e.toLowerCase()).includes(email)) return null;
            }
            const isActive = navKey === mod.key || mod.subItems?.some((s) => s.key === navKey);
            const expanded = expandedGroups[mod.key];
            // v1.7.40 (QB v18, 2026-08-07) — real, active-state styling was
            // gated on `!mod.subItems`, which correctly excluded items that
            // ONLY toggle (no real page of their own) but also wrongly
            // excluded 'storage' now that it has both subItems AND a real
            // component. hasOwnPage is true for any item that genuinely
            // navigates -- either no subItems at all, or subItems + a real
            // component (the storage case).
            const hasOwnPage = !mod.subItems || !!mod.component;

            // v1.7.40 (QB v18, 2026-08-06) — real, the gold nav-group
            // divider this tracked is now removed (Shane's feedback --
            // read as an unwanted/confusing line against the flat
            // sidebar color). renderedGroups/isNewGroup/isFirstGroup
            // existed solely to place that divider -- removed as dead
            // code along with it, not left behind unused.

            return (
              <div key={mod.key} id={`navgroup-${mod.key}`}>
                {/* #1797 -- real, Stinger direct 2026-08-23, approved live
                    via mockup iteration ("looks nice lets build it").
                    Deliberately subtle -- reuses the exact label styling
                    already proven in the mobile "All Modules" menu below,
                    NOT the bolder colored divider line that was removed
                    from this exact spot before (v1.7.40 QB v18 comment
                    above: "read as an unwanted/confusing line against the
                    flat sidebar color"). A label-only treatment with a
                    faint hairline is a real, different, lighter-weight
                    thing than what was rejected then. */}
                {!sidebarCollapsed && mod.sectionLabel && (
                  <div style={{ padding: '14px 20px 6px', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--sidebar-text-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', whiteSpace: 'nowrap' }}>
                      {mod.sectionLabel}
                    </span>
                    <span style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.06)' }} />
                  </div>
                )}
                {/* v1.7.40 (QB v18, 2026-08-06) — real, the gold nav-group
                    divider this tracked is now removed (Shane's feedback --
                    read as an unwanted/confusing line against the flat
                    sidebar color). renderedGroups/isNewGroup/isFirstGroup
                    existed solely to place that divider -- removed as dead
                    code along with it, not left behind unused. */}
                <div
                  onClick={() => {
                    // v1.7.40 (QB v18, 2026-08-07) — real, fixes a confirmed
                    // live bug: 'storage' (Browse Files) is the only real,
                    // visible nav item with BOTH subItems (the real accordion
                    // showing Images/Documents/Videos/Recordings) and its own
                    // real component (the new landing page). The old rule
                    // toggled the accordion and stopped there, so clicking
                    // Browse Files never actually navigated -- the main
                    // content area kept showing whatever page was there
                    // before. Real fix: when both exist, do both -- navigate
                    // to the real landing page AND expand the accordion, so
                    // the sub-items are still reachable too. HUNT and
                    // Settings are unaffected -- neither has this combination
                    // (their sub-tools are hidden top-level routes, not
                    // visible subItems).
                    if (sidebarCollapsed && mod.subItems) {
                      setSidebarCollapsed(false);
                      try { localStorage.setItem('charlie.sidebar.collapsed.v1', 'false'); } catch (_) { /* storage can be unavailable */ }
                      toggleGroup(mod.key);
                      if (!mod.component) return;
                    }
                    if (mod.subItems && !mod.component) { toggleGroup(mod.key); return; }
                    if (mod.subItems && mod.component) { toggleGroup(mod.key); }
                    // v1.7.39.150 — Shane's explicit, repeated instruction:
                    // the top-left Dashboard nav item should go straight to
                    // MY personal dashboard, one click, not require digging
                    // into the top-right chip's dropdown. Only this specific
                    // nav item does this -- every other module navigates
                    // exactly as it always has.
                    if (mod.key === 'dashboard') setViewMode('personal');
                    // Real, 2026-08-06: a genuine re-click of the already-
                    // active item forces a real remount (see remountNonce
                    // above) -- fixes modules that can't otherwise get back
                    // to their own real landing view.
                    if (mod.key === activeKey) setRemountNonce((n) => n + 1);
                    noteMenuNavigation(); // #2771: a sidebar click is a fresh visit: no list restores its old place
                    navigate(mod.key);
                  }}
                  title={sidebarCollapsed ? mod.label : undefined}
                  aria-label={mod.label}
                  style={{ padding: '7px 20px', paddingLeft: sidebarCollapsed ? 19 : 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: sidebarCollapsed ? 'center' : 'flex-start', gap: 12, background: isActive && hasOwnPage ? 'rgba(255,255,255,0.08)' : 'transparent', fontWeight: isActive && hasOwnPage ? 700 : undefined, transition: 'all 0.1s' }}
                >
                  {/* v1.7.39.192 — real fix: mod.icon was being passed straight
                      into an SVG path `d` attribute, but every registry entry
                      holds a placeholder string like 'ICONS.search' -- not real
                      path data. There is no ICONS lookup object anywhere in the
                      codebase, so this render was inert for every module the
                      whole time. Real fix: if mod.icon is an image path (new
                      PNG icon set), render it as an <img>. Otherwise fall back
                      to the old <path> behavior unchanged -- zero regression
                      for anything not yet given a real image icon. */}
                  {/* v1.7.41.80 (Ledger #1701) -- real, per Stinger 2026-08-12:
                      nav icons were taking up too much of the panel. Shrunk
                      38px -> 26px, row padding 9px -> 7px vertical. Real
                      reduction in actual content height rather than an
                      artificial max-height cap that would clip real nav
                      items if the module list ever grows past what fits. */}
                  <span aria-hidden="true" style={{ width: 8, flexShrink: 0 }} />
                  {!sidebarCollapsed && <span style={{ flex: 1, fontSize: 13, color: isActive && hasOwnPage ? '#ffffff' : 'var(--sidebar-text)', fontWeight: isActive && hasOwnPage ? 600 : 400, textTransform: genservMenu || mod.key === 'repd-launcher' ? 'none' : 'uppercase', letterSpacing: genservMenu || mod.key === 'repd-launcher' ? 'normal' : '0.03em' }}>{mod.label}</span>}
                  {!sidebarCollapsed && genservMenu && mod.key === 'genserv-outbox' && <OutboxMenuBadge />}
                  {!sidebarCollapsed && mod.subItems && (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="#8b9ab0" style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
                      <path d="M7 10l5 5 5-5z" />
                    </svg>
                  )}
                </div>
                {!sidebarCollapsed && mod.subItems && expanded && mod.subItems.filter((s) => !s.hidden).map((sub) => (
                  <div
                    key={sub.key}
                    onClick={() => clickSubItem(sub)}
                    style={{ padding: '8px 20px 8px 44px', cursor: 'pointer', fontSize: 12, color: isSubItemActive(sub) ? '#ffffff' : 'var(--sidebar-text)', background: isSubItemActive(sub) ? 'rgba(255,255,255,0.08)' : 'transparent', transition: 'all 0.1s' }}
                  >
                    {sub.label}
                  </div>
                ))}
              </div>
            );
          })}
          {genservShellOn && (
            <div
              role="button" tabIndex={0} data-testid="back-to-charlie"
              onClick={backToCharlie}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); backToCharlie(); } }}
              title={sidebarCollapsed ? 'Back to Charlie' : undefined}
              style={{ margin: '18px 0 0', padding: '10px 20px', paddingLeft: sidebarCollapsed ? 19 : 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: sidebarCollapsed ? 'center' : 'flex-start', gap: 12, borderTop: '1px solid rgba(255,255,255,0.08)' }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="#c8d6e5" style={{ flexShrink: 0 }}><path d="M20 11H7.8l5.6-5.6L12 4l-8 8 8 8 1.4-1.4L7.8 13H20v-2z" /></svg>
              {!sidebarCollapsed && <span style={{ flex: 1, fontSize: 12, color: 'var(--sidebar-text-dim, #8b9ab0)', letterSpacing: '0.03em' }}>Back to Charlie</span>}
            </div>
          )}
        </nav>

        <SidebarTonePicker collapsed={sidebarCollapsed} />

        <button type="button" aria-label={sidebarCollapsed ? 'Expand navigation' : 'Collapse navigation'} aria-expanded={!sidebarCollapsed} onClick={() => setSidebarCollapsed((value) => { const next = !value; try { localStorage.setItem('charlie.sidebar.collapsed.v1', String(next)); } catch (_) { /* storage can be unavailable */ } return next; })} style={{ width: '100%', minHeight: 38, border: 0, borderTop: '1px solid rgba(255,255,255,0.08)', color: 'var(--sidebar-text)', background: 'transparent', cursor: 'pointer', textAlign: sidebarCollapsed ? 'center' : 'right', flexShrink: 0, padding: sidebarCollapsed ? 8 : '8px 16px' }} title={sidebarCollapsed ? 'Expand navigation' : 'Collapse navigation'}><span aria-hidden="true" data-testid="sidebar-collapse-arrow" style={{ fontSize: 20, lineHeight: 1 }}>{sidebarCollapsed ? '›' : '‹'}</span></button>

        {/* v1.7.41.81 (Ledger #1702) -- real, removed per Stinger 2026-08-12:
            "frumpy," "looks like it belongs with a different system." The
            Switch User functionality that lived here (SidebarSwitchUser.jsx,
            super_admin-only) moved into the top-right UserMenu dropdown
            instead -- same real viewAsUserId/setViewAsUserId plumbing, same
            gating, just relocated per his exact ask: built into the name
            click, "very inconspicuous, very low key," not a separate
            styled control. SidebarSwitchUser.jsx itself left in place
            (unused) rather than deleted -- a real, working component with
            real history (see The Relay: three prior placement attempts)
            is safer kept than removed outright in case this needs to
            revert. */}

        {/* Version badge — inconspicuous, click to open About */}
        {/* v1.7.40.92 -- version badge + health dot MOVED into the
            SD/Shane dropdown (UserMenu.jsx), per Shane's sidebar-cleanup
            direction. Removed here rather than duplicated. */}

      </div>

      {/* ── MAIN WORKSPACE ── */}
      <div className="charlie-workspace" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div data-testid="top-bar" style={{ height: isMobile ? 56 : 72, background: '#ffffff', borderBottom: '1px solid var(--gov-slate-200)', display: 'flex', alignItems: 'center', padding: isMobile ? '0 8px 0 12px' : '0 24px', flexShrink: 0, gap: isMobile ? 8 : 16, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <PartnerHeaderLogo partner={activePartner} isMobile={isMobile} />
            {/* #2047 -- real removal, Stinger direct 2026-09-07: this
                "C.H.A.R.L.I.E." wordmark duplicated the sidebar's own
                persistent CHARLIE identity (top of sidebar, ~line 872).
                Its real combo action (switch to Well Repd + navigate to
                dashboard) is preserved via two real, already-existing
                controls instead of one hidden dual-purpose logo click:
                HeaderPersonaChip below covers switching persona, the
                sidebar's own "Home" nav item (key: 'dashboard') covers
                navigation. Neither is new functionality -- both already
                existed, this just stops relying on an undiscoverable
                wordmark click to do both at once. */}
            {/* v1.7.18 — version tag for mobile, where the sidebar footer
                copy is hidden behind the collapsed nav. v1.7.39.144 —
                real fix: this was showing on desktop too, right next to
                the sidebar's own version+health-dot display, reading as
                a flat-out duplicate. Desktop already has the sidebar
                copy; only mobile needs this one. */}
            {false && isMobile && ( // the version badge is hidden on a phone (the header is one clean row; the version is in About)
              <span onClick={() => navigate('about')} title="Release notes" style={{ fontSize: 10, fontWeight: 700, color: 'var(--gov-slate-400)', background: 'var(--gov-slate-100)', border: '1px solid var(--gov-slate-200)', borderRadius: 4, padding: '1px 5px', cursor: 'pointer', flexShrink: 0 }}>v{APP_VERSION}</span>
            )}
            {activeKey !== 'dashboard' && (() => {
              // Breadcrumb: REAL pages only (never a cluster heading or an old registry group), each clickable except the last. A page with a parent also gets a Back link
              // (on a phone the Back link is all there is, since the header is one row).
              const crumbs = crumbsOf(activeKey);
              const up = parentKey(activeKey);
              return (
                <>
                  {up && (
                    <button type="button" data-testid="back-link" onClick={() => { if (backToOrigin(window.history, navigate, activeKey) === 'none') navigate(up); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: color, fontSize: 14, fontWeight: 700, padding: 0, minHeight: isMobile ? 44 : undefined, whiteSpace: 'nowrap' }}>{`← ${crumbs[crumbs.length - 2].label}`}</button>
                  )}
                  {!isMobile && crumbs.length > 0 && (
                    <nav aria-label="Breadcrumb" data-testid="breadcrumb" style={{ fontSize: 13, color: 'var(--gov-slate-400)', display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                      {withCrumbTitle(crumbs, crumbTitleNow).map((c, i, all) => (i < all.length - 1
                        ? <React.Fragment key={c.key}><button type="button" onClick={() => { if (c.key === activeKey) setRemountNonce((n) => n + 1); navigate(c.key); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gov-slate-500)', fontSize: 13, padding: 0 }}>{c.label}</button><span>/</span></React.Fragment>
                        : <span key={c.key} aria-current="page" style={{ color: 'var(--gov-slate-700)', fontWeight: 600 }}>{c.label}</span>))}
                    </nav>
                  )}
                </>
              );
            })()}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 14, flexShrink: 0, minWidth: 0 }}>
            {/* #2047 -- real revival, Stinger direct 2026-09-07: persona
                switching needs to be a compact, obvious header-level
                control, not buried inside the account dropdown. Same
                real activePartners/activePartner/switchPartner already
                passed to UserMenu below -- no new state, no new data
                fetch. UserMenu's own Switch Persona section (with the
                real drag-reorder/hide/quick-pick Manage mode) is left
                untouched -- that curation UI stays in the account area,
                this chip is fast-switch only. */}
            <HeaderPersonaChip
              partners={activePartners.filter((partner) => !isGenservPartner(partner))}
              activePartner={activePartner}
              userId={currentUser?.user?.id}
              onSwitchPartner={(p) => switchPartner(p)}
            />
            {!isMobile && <HeaderQuickOrder />}
            {/* v1.7.18 — hidden on mobile (the Phoenix card already shows the date; header was crowded) */}
            <div className="charlie-header-date" style={{ fontSize: 12, color: 'var(--sidebar-text-muted)', whiteSpace: 'nowrap' }}>
              {new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
            </div>
            {/* v1.7.40.92 -- the SD/Shane trigger stays visually identical,
                but now opens a real dropdown (date header, Settings, Sign
                Out, version+health) instead of showing a bare Sign out
                button. Sign Out and the version badge both MOVED here --
                see UserMenu.jsx. */}
            {!isMobile && (
            <UserMenu
              currentUser={currentUser}
              activePartner={activePartner}
              appVersion={APP_VERSION}
              appGitSha={APP_GIT_SHA}
              appBuildTitle={APP_BUILD_TITLE}
              onNavigate={navigate}
              onSignOut={onSignOut}
              viewAsUserId={viewAsUserId}
              setViewAsUserId={setViewAsUserId}
              onGoPersonal={() => { setViewMode('personal'); navigate('dashboard'); }}
              viewMode={viewMode}
              partners={activePartners}
              allPartners={partners}
              onPartnersChanged={loadPartners}
              onSwitchPartner={(p) => switchPartner(p)}
            />
            )}
            {isMobile && !genservShellOn && (
              <button type="button" data-testid="phone-more-button" onClick={() => setMoreMenu((m) => !m)} style={{ minHeight: 44, padding: '0 14px', borderRadius: 22, border: '1px solid var(--gov-border-soft, #cbd5e1)', background: '#fff', fontSize: 15, fontWeight: 700, color: 'var(--gov-ink, #1a2133)', cursor: 'pointer' }}>More</button>
            )}
          </div>
        </div>
        <div className="charlie-main-content" style={{ flex: 1, overflowY: 'auto', background: '#f0f2f5' }}>
          <Suspense fallback={<div style={{ padding: 32, color: 'var(--sidebar-text-muted)' }}>Loading module...</div>}>
            <ModuleErrorBoundary resetKey={activeKey}>
              {(() => {
                const page = ActiveComponent
                  ? <AppRouteGate memberships={appMemberships} activeKey={activeKey} isGenservKey={inGenservContext} onNavigate={navigate}><ActiveComponent key={`${activeKey}-${remountNonce}`} onNavigate={navigate} routeParams={routeParams} currentUser={currentUser} /></AppRouteGate>
                  : <div style={{ padding: 32, color: 'var(--sidebar-text-muted)' }}>Module not found: {activeKey}</div>;
                return isMobile ? page : <PageContainer>{page}</PageContainer>; // every desktop page sits in the centered frame; phone layouts are unchanged
              })()}
            </ModuleErrorBoundary>
          </Suspense>
        </div>
      </div>

      {/* ── MOBILE BOTTOM NAV ── */}
      <div className="charlie-bottom-nav">
        {(isMobile && genservShellOn ? GENSERV_PHONE_NAV : MOBILE_NAV).map((item) => {
          const isActive = item.match ? item.match(activeKey) : item.isPersona ? personaSwitcher : item.isMore ? moreMenu : activeKey === item.key;
          return (
            <div
              key={item.key}
              onClick={() => {
                if (item.isPersona) { setPersonaSwitcher((p) => !p); setMoreMenu(false); }
                else if (item.isMore) { setMoreMenu((p) => !p); setPersonaSwitcher(false); }
                else if (item.isExternal) { window.open(item.url, '_blank'); }
                else {
                  // v1.7.39.150 — same fix as the desktop sidebar: mobile's
                  // Home/Dashboard button goes straight to the personal view.
                  if (item.key === 'dashboard') setViewMode('personal');
                  // Real, 2026-08-06: same remount fix as desktop sidebar.
                  if (item.key === activeKey) setRemountNonce((n) => n + 1);
                  noteMenuNavigation(); // #2771
                  navigate(item.key, item.key === 'lead-management' ? { tab: 'ready' } : {});
                  setPersonaSwitcher(false);
                  setMoreMenu(false);
                }
              }}
              style={{ position: 'relative', flex: 1, minHeight: 44, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, cursor: 'pointer', color: isActive ? color : 'var(--sidebar-text-muted)', borderTop: isActive ? `2px solid ${color}` : '2px solid transparent' }}
            >
              {item.isPersona && activePartner ? (
                <div style={{ width: 22, height: 22, borderRadius: '50%', background: `linear-gradient(135deg, ${color}, ${activePartner.secondary_color || color})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 10 }}>
                  {activePartner.name[0]}
                </div>
              ) : (
                item.isImage ? (
                  <img src={item.icon} alt="" width={20} height={20} style={{ objectFit: 'contain', opacity: isActive ? 1 : 0.75 }} />
                ) : item.stroke ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={isActive ? color : '#8b9ab0'} strokeWidth="2" strokeLinecap="round"><path d={item.icon} /></svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill={isActive ? color : '#8b9ab0'}>
                    <path d={item.icon} />
                  </svg>
                )
              )}
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.01em', whiteSpace: 'nowrap' }}>{item.isPersona ? (activePartner?.name?.split(' ')[0] || 'Persona') : item.label}</span>
              {item.badge && qualifiedCount != null && qualifiedCount > 0 && <span data-testid="leads-badge" style={{ position: 'absolute', top: 4, left: '50%', marginLeft: 8, minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9, background: 'var(--gov-slate-200)', color: 'var(--gov-accent-slate)', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}>{compactCount(qualifiedCount)}</span>}
            </div>
          );
        })}
      </div>

      {/* Phone: no side menu. A short More sheet (opened from the header) holds the few extras; the bottom bar, Dashboard and the + button are the app. */}
      {isMobile && <PhoneMoreSheet open={moreMenu} onClose={() => setMoreMenu(false)} onNavigate={(k, p) => navigate(k, p || {})} onSignOut={onSignOut} />}

      {/* Mobile persona switcher drawer */}
      {personaSwitcher && (
        <div onClick={() => setPersonaSwitcher(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 490, display: 'none' }} className="mobile-persona-backdrop">
          <div onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', bottom: 60, left: 0, right: 0, background: 'var(--sidebar-bg-deep)', borderTop: '1px solid var(--sidebar-line)', borderRadius: '16px 16px 0 0', maxHeight: '60vh', overflowY: 'auto', padding: '16px 0' }}>
            <div style={{ padding: '0 16px 12px', fontSize: 11, fontWeight: 700, color: 'var(--sidebar-text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Switch Persona</div>
            {Object.entries(grouped).map(([group, list]) => list.length === 0 ? null : (
              <div key={group}>
                <div style={{ padding: '6px 16px', fontSize: 10, fontWeight: 700, color: 'var(--sidebar-text-dim)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{group}</div>
                {list.map((p) => (
                  <div key={p.id} onClick={() => { switchPartner(p); setPersonaSwitcher(false); }} style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', background: p.id === activePartner?.id ? (p.brand_color || color) + '22' : 'transparent' }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: p.brand_color || 'var(--sidebar-text-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>{p.name[0]}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#ffffff' }}>{p.name}</div>
                      {p.email && <div style={{ fontSize: 11, color: 'var(--sidebar-text-muted)' }}>{p.email}</div>}
                    </div>
                    {p.id === activePartner?.id && <div style={{ width: 8, height: 8, borderRadius: '50%', background: p.brand_color || color }} />}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── QUICK SCOUT FLOATER (v1.6.87 · B23) ── */}
      {showQuickFloater({ isMobile, partner: activePartner }) && <QuickScoutFloater onNavigate={navigate} />}
      <QuickOrderHost />
      <UpdateToast />
      <StaleBanner />

      {/* v1.7.40.44 -- real, Shane's repeated ask: this floating icon was
          too easy to miss (bottom-right, small, quiet). Switch User lives
          in the SD/Shane UserMenu dropdown's "Viewing as" section instead
          (UserMenu.jsx), the one place Shane already looks every time.
          Removed here rather than duplicated in two places.
          #1805 -- comment updated: originally pointed at HeaderPersonaChip,
          which is itself removed now (its own, separate real concept --
          partner switching -- also folded into UserMenu, as its own
          "Switch Persona" section). Both real features now live in the
          same dropdown, in their own distinct sections. */}

      {/* ── TOAST ── */}
      {toast && (
        <div style={{
          position: 'fixed',
          top: toast.type === 'info' ? 20 : 'auto',
          bottom: toast.type === 'info' ? 'auto' : 24,
          right: 24,
          zIndex: 999,
          background: toast.type === 'error' ? '#2a1a1a' : toast.type === 'info' ? 'var(--sidebar-bg-deep)' : '#1a2a1a',
          border: `1px solid ${toast.type === 'error' ? '#e06c75' : toast.type === 'info' ? '#5a7ab0' : '#4ade80'}`,
          color: toast.type === 'error' ? '#e06c75' : toast.type === 'info' ? '#a0b4d0' : '#4ade80',
          borderRadius: 8,
          padding: '10px 18px',
          fontSize: 13,
          fontWeight: 600,
          boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
          animation: 'slideIn 0.2s ease',
          cursor: 'pointer',
        }} role="status" data-testid="app-toast" onClick={dismissToast} {...swipeDismiss(dismissToast)}>
          {toast.message}
        </div>
      )}
    </div>
  );
}
